import { isModulrAccount } from '@/utils/accounts/is-modulr-account';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { Button } from '../../../../design-system/button/button';
import { Form } from '../../../../design-system/form/form-primitives';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountBankingTypeView, AccountView } from '../../../../types/entity-types';
import { BankAccountMapping } from '../../integrations-page-wrapper';
import { AccountMappingTable, XeroBankAccountView } from './account-mapping-table';

const getAccountMappingFormSchema = (accounts: AccountView[]) =>
  z.object({
    accounts: z.array(
      z
        .object({
          accountId: z.string(),
          xeroAccountId: z.string(),
          fromDate: z.string(),
        })
        .superRefine(({ accountId, fromDate, xeroAccountId }, ctx) => {
          const account = accounts.find((a) => a.id === accountId);

          if (!account || !account.fspAdapter) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Account not found among the accounts to map',
              path: ['fromDate'],
            });

            return;
          }

          // Only validate fromDate if the account has been mapped
          if (xeroAccountId && isModulrAccount(account) && !fromDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Date is required for mapped accounts',
              path: ['fromDate'],
            });

            return;
          }
        }),
    ),
  });

export type AccountMappingFormValues = z.infer<ReturnType<typeof getAccountMappingFormSchema>>;

export type ExternalBankAccount = {
  id: string;
  name: string;
  currency: string;
};

/**
 * Response type for bank accounts endpoint
 * Includes rate limit status when Xero API limit is hit
 */
export type GetBankAccountsResponse = {
  accounts: ExternalBankAccount[];
  isRateLimited?: boolean;
  retryAfterSeconds?: number;
  cachedAt?: string;
};

type AccountMappingFormProps = {
  accounts: AccountBankingTypeView[];
  xeroBankAccounts: XeroBankAccountView[];
  xeroAccountMappings: BankAccountMapping[];
  preselectedAccountId?: string;
  isLoading: boolean;
  isCreatingXeroAccount: boolean;
  onCreateXeroAccount: (seapointAccountId: string) => Promise<string | undefined>;
  onSubmit: (values: AccountMappingFormValues['accounts']) => void;
};

export const AccountMappingForm = ({
  accounts,
  xeroBankAccounts,
  xeroAccountMappings,
  preselectedAccountId,
  isLoading,
  isCreatingXeroAccount,
  onCreateXeroAccount,
  onSubmit,
}: AccountMappingFormProps) => {
  // If preselectedAccountId is provided, filter to show only that account
  const filteredAccounts = preselectedAccountId
    ? accounts.filter((account) => account.id === preselectedAccountId)
    : accounts;

  const form = useForm<AccountMappingFormValues>({
    resolver: zodResolver(getAccountMappingFormSchema(filteredAccounts)),
    defaultValues: {
      accounts: filteredAccounts.map((account) => {
        const xeroAccountMapping = xeroAccountMappings.find((mapping) => mapping.seapointAccountId === account.id);

        return {
          accountId: account.id,
          xeroAccountId: xeroAccountMapping?.externalAccountId || '',
          fromDate: xeroAccountMapping?.syncFromDate || '',
        };
      }),
    },
    mode: 'onTouched',
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(({ accounts }) => {
          // Only submit accounts that have been mapped (have a xeroAccountId)
          const mappedAccounts = accounts
            .filter((account) => account.xeroAccountId)
            .map((account) => ({
              ...account,
              // Omit empty syncFromDate (non-Modulr accounts don't need it)
              syncFromDate: account.fromDate || undefined,
            }));
          onSubmit(mappedAccounts);
        })}
      >
        <Stack>
          <AccountMappingTable
            accounts={filteredAccounts}
            xeroBankAccounts={xeroBankAccounts}
            isCreatingXeroAccount={isCreatingXeroAccount}
            onCreateXeroAccount={onCreateXeroAccount}
          />

          <Inline justify="end" className="w-full">
            <Button type="submit" variant="default" isLoading={isLoading}>
              Continue
            </Button>
          </Inline>
        </Stack>
      </form>
    </Form>
  );
};
